'use strict';

/**
 * Bank Calculator - handles tab switching and payment calculation
 * Uses decimal.js with precision 28 for accurate calculations
 */

import Decimal from 'decimal.js';

// Import mock data for fallback
import mockPaymentData from './mock-data.js';

// Set precision for all decimal calculations
Decimal.set({ precision: 28 });

/**
 * Parse period steps string into array of periods
 * @param {string} periodStepsStr - e.g., "12, 6-24, 3-12"
 * @returns {number[]}
 */
function parsePeriodSteps(periodStepsStr) {
    const periods = new Set();
    const parts = periodStepsStr.split(',').map(s => s.trim());
    
    parts.forEach(part => {
        if (part.includes('-')) {
            const [min, max] = part.split('-').map(Number);
            // Use min from range as the step (e.g., "6-24" uses step 6, "3-12" uses step 3)
            for (let i = min; i <= max; i += min) {
                periods.add(i);
            }
        } else {
            periods.add(Number(part));
        }
    });
    
    return Array.from(periods).sort((a, b) => a - b);
}

/**
 * Generate initial payment percentages (starting from 0)
 * @param {number} step - step percentage
 * @param {number} max - maximum percentage
 * @returns {number[]}
 */
function generateInitialPayments(step, max) {
    const payments = [];
    for (let i = 0; i <= max; i += step) {
        payments.push(i);
    }
    return payments;
}

/**
 * Calculate annuity payment
 * @param {Decimal} i - monthly interest rate
 * @param {Decimal} n - number of periods
 * @param {Decimal} s - principal amount
 * @returns {Decimal}
 */
function annuity(i, n, s) {
    if (i.equals(0)) {
        return s.dividedBy(n).neg();
    }
    i = i.dividedBy(100).dividedBy(12);
    const one = new Decimal(1);
    const numerator = i.times(one.plus(i).pow(n));
    const denominator = one.plus(i).pow(n).minus(one);
    return s.times(numerator.dividedBy(denominator)).neg();
}

/**
 * Calculate payment for a product
 * @param {number} remainingPrice - price minus initial payment
 * @param {Object} product - bank product
 * @returns {Object} - product with calculated values
 */
function calculateProductPayment(remainingPrice, product, actualPeriod) {
    const decimalRemaining = new Decimal(remainingPrice);
    const decimalRate = new Decimal(product.rate || 0);
    // Use actualPeriod (selected by user) instead of product.period for early repayment
    const decimalPeriod = new Decimal(actualPeriod || product.period);
    const decimalGraceRate = new Decimal(product.graceRate || 0);
    const decimalGracePeriod = new Decimal(product.gracePeriod || 0);
    const additionalDiscount = product.additionalDiscount || 0;
    
    // Apply additional discount/markup to remaining price
    // Negative = discount (reduces price), Positive = markup (increases price)
    let effectivePrice = decimalRemaining;
    let discountAmount = new Decimal(0);
    
    if (additionalDiscount !== 0) {
        discountAmount = decimalRemaining.times(new Decimal(additionalDiscount)).dividedBy(100);
        effectivePrice = decimalRemaining.plus(discountAmount);
    }
    
    let totalPayment, monthlyPayment;
    
    if (product.type === 'leasing') {
        // Leasing: price/period + price*rate/100
        monthlyPayment = effectivePrice.dividedBy(decimalPeriod).plus(
            effectivePrice.dividedBy(100).times(decimalRate)
        );
        totalPayment = monthlyPayment.times(decimalPeriod);
    } else if (product.type === 'installment' || product.type === 'halva') {
        // Installment/Halva: price/period + tiny interest from rate
        // Even with very small rate (0.0001), there's slight interest
        const monthlyRate = decimalRate.dividedBy(100).dividedBy(12); // Convert annual rate to monthly
        if (monthlyRate.greaterThan(0)) {
            // Use annuity formula with tiny rate
            monthlyPayment = annuity(decimalRate, decimalPeriod, effectivePrice).neg();
        } else {
            monthlyPayment = effectivePrice.dividedBy(decimalPeriod);
        }
        // Total payment = monthly * period
        totalPayment = monthlyPayment.times(decimalPeriod);
    } else if (product.type === 'credit' || product.type === 'credit_combo') {
        // Credit: annuity calculation
        monthlyPayment = annuity(decimalRate, decimalPeriod, effectivePrice).neg();
        totalPayment = monthlyPayment.times(decimalPeriod);
    } else if (product.type === 'credit_grace') {
        // Credit with grace period
        if (decimalGracePeriod.greaterThanOrEqualTo(decimalPeriod)) {
            monthlyPayment = annuity(decimalRate, decimalPeriod, effectivePrice).neg();
            totalPayment = monthlyPayment.times(decimalPeriod);
        } else {
            // Calculate during grace period
            const remainingPriceGrace = effectivePrice.dividedBy(decimalPeriod).times(decimalGracePeriod);
            const graceMonthlyPayment = annuity(decimalGraceRate, decimalGracePeriod, remainingPriceGrace).neg();
            const totalGracePayment = graceMonthlyPayment.times(decimalGracePeriod);
            
            // Calculate after grace period
            const afterGraceRemaining = effectivePrice.minus(totalGracePayment);
            const afterGracePeriods = decimalPeriod.minus(decimalGracePeriod);
            const afterGracePayment = annuity(decimalRate, afterGracePeriods, afterGraceRemaining).neg();
            const totalAfterGrace = afterGracePayment.times(afterGracePeriods);
            
            totalPayment = totalGracePayment.plus(totalAfterGrace);
            monthlyPayment = totalPayment.dividedBy(decimalPeriod);
        }
    } else {
        // Default to simple division
        monthlyPayment = effectivePrice.dividedBy(decimalPeriod);
        totalPayment = monthlyPayment.times(decimalPeriod);
    }
    
    const overpayment = totalPayment.minus(decimalRemaining);
    
    return {
        ...product,
        monthlyPayment: parseFloat(monthlyPayment.toFixed(2, Decimal.ROUND_HALF_UP)),
        totalPayment: parseFloat(totalPayment.toFixed(2, Decimal.ROUND_HALF_UP)),
        overpayment: parseFloat(overpayment.toFixed(2, Decimal.ROUND_HALF_UP)),
        discountAmount: parseFloat(discountAmount.toFixed(2, Decimal.ROUND_HALF_UP)),
        effectivePrice: parseFloat(effectivePrice.toFixed(2, Decimal.ROUND_HALF_UP))
    };
}

/**
 * Find product with minimum overpayment from array
 * @param {Object[]} products
 * @returns {Object}
 */
function findMinOverpaymentProduct(products) {
    if (!products || products.length === 0) return null;
    return products.reduce((min, p) => 
        p.overpayment < min.overpayment ? p : min, products[0]);
}

/**
 * Main BankCalculator class
 */
class BankCalculator {
    constructor(element) {
        this.element = element;
        this.price = parseFloat(element.dataset.price) || 0;
        this.brand = element.dataset.brand || '';
        
        // Default values from data attributes (for pre-selecting options)
        this.defaultBankProductGroup = element.dataset.bankProductGroup || '';
        this.defaultProductType = element.dataset.productType || ''; // product name/type for radio button
        this.defaultInitialFee = parseInt(element.dataset.initialFee) || 0;
        this.defaultPeriod = parseInt(element.dataset.period) || 0;
        
        this.ajaxData = null;
        
        // Cache DOM elements
        this.tabs = element.querySelectorAll('.bank-calculator__tab');
        this.panels = element.querySelectorAll('.bank-calculator__panel');
        this.priceEl = element.querySelector('.js-bank-calculator-price');
        this.initialEl = element.querySelector('.js-bank-calculator-initial');
        this.periodEl = element.querySelector('.js-bank-calculator-period');
        this.monthlyEl = element.querySelector('.js-bank-calculator-monthly');
        this.totalEl = element.querySelector('.js-bank-calculator-total');
        this.discountEl = element.querySelector('.js-bank-calculator-discount');
        this.discountRowEl = element.querySelector('.js-bank-calculator-discount-row');
        
        // Initialize discount row as hidden
        if (this.discountRowEl) {
            this.discountRowEl.style.display = 'none';
        }

        // Mark as loading: values are hidden by CSS until AJAX data is applied,
        // so the popup can never show placeholder zeros instead of real numbers
        this.element.classList.add('bank-calculator--loading');

        // Current state
        this.activeTab = 'installment';
        this.currentType = null;
        this.currentPeriod = null;
        this.currentInitial = null;
        
        // Store values for each tab (to remember when switching)
        this.tabStates = {};
        
        // Initial payment blocking state
        this.savedInitialPayment = null;
        this.isInitialPaymentBlocked = false;
        
        // Promise that resolves when init() completes (AJAX data loaded + UI rendered)
        this._readyPromise = new Promise(resolve => { this._readyResolve = resolve; });
        
        // Initialize
        this.init();
    }
    
    async init() {
        try {
            // Fetch AJAX data
            await this.fetchData();

            // Hide tabs without products and activate first tab with products
            this.hideTabsWithoutProducts();

            // Apply default values from data attributes before activating tab
            this.applyDefaultValues();

            this.activateFirstTabWithProducts();

            // Setup event listeners
            this.setupTabListeners();
            this.setupFormListeners();

            // Initial render
            this.renderTabOptions();

            // Apply defaults again after render to ensure they're set
            this.applyDefaultValues();

            this.calculate();
        } finally {
            // Data applied (or failed): reveal values. Event-driven, no timers.
            this.element.classList.remove('bank-calculator--loading');
        }

        // Signal that the calculator is fully initialized
        if (this._readyResolve) {
            this._readyResolve();
            this._readyResolve = null;
        }
    }
    
    /**
     * Apply default values from data attributes (bankProductGroup, productType/groupId, initialFee, period)
     * productType can be either: groupId (e.g., "skrepka") or displayName (e.g., "Мультибанковская система \"Скрепка\"")
     */
    applyDefaultValues() {
        if (!this.ajaxData) return;
        
        // Map bankProductGroup to tab name
        const groupToTab = {
            'installment': 'installment',
            'credit': 'credit',
            'halva': 'halva',
            'leasing': 'leasing'
        };
        
        // First, set tab from bankProductGroup if provided
        if (this.defaultBankProductGroup) {
            const tabFromGroup = groupToTab[this.defaultBankProductGroup.toLowerCase()];
            if (tabFromGroup) {
                // Verify this tab has products
                const products = this.getProductsForTab(tabFromGroup);
                if (products.length > 0) {
                    this.activeTab = tabFromGroup;
                }
            }
        }
        
        // Then, set product type from productType (groupId or displayName)
        if (this.defaultProductType) {
            // Check if it's a groupId - try to find displayName from groupNames
            const displayNameFromGroupId = this.ajaxData.groupNames?.[this.defaultProductType];
            
            if (displayNameFromGroupId) {
                // It's a groupId - use displayName as currentType
                this.currentType = displayNameFromGroupId;
                
                // Also update tab if not already set from bankProductGroup
                if (!this.activeTab) {
                    const tabNames = ['installment', 'credit', 'halva', 'leasing'];
                    for (const tabName of tabNames) {
                        const products = this.getAllProductsForTab(tabName);
                        const hasGroup = products.some(p => p.groupId === this.defaultProductType);
                        if (hasGroup) {
                            this.activeTab = tabName;
                            break;
                        }
                    }
                }
            } else {
                // It's already a displayName - use as is
                this.currentType = this.defaultProductType;
                
                // Also find the correct tab if not set
                if (!this.activeTab) {
                    const tabNames = ['installment', 'credit', 'halva', 'leasing'];
                    for (const tabName of tabNames) {
                        const products = this.getProductsForTab(tabName);
                        const groupedProducts = this.getGroupedProducts(products);
                        const hasType = groupedProducts.some(p => p.name === this.defaultProductType);
                        if (hasType) {
                            this.activeTab = tabName;
                            break;
                        }
                    }
                }
            }
        }
        
        // If default period is set, store it (will be applied in handlePeriodForTypeChange or renderTabOptions)
        if (this.defaultPeriod > 0) {
            this.currentPeriod = this.defaultPeriod;
        }
        
        // If default initial fee is set, store it
        if (this.defaultInitialFee > 0) {
            this.currentInitial = this.defaultInitialFee;
        }
    }
    
    /**
     * Hide tabs that don't have products for current price
     * Shows tabs AFTER AJAX when products exist in that tab
     */
    hideTabsWithoutProducts() {
        const tabNames = ['installment', 'credit', 'halva', 'leasing'];
        let hasAnyProducts = false;
        
        tabNames.forEach(tabName => {
            const products = this.getProductsForTab(tabName);
            const tab = this.element.querySelector(`[data-tab="${tabName}"]`);
            const panel = this.element.querySelector(`[data-panel="${tabName}"]`);
            
            if (products.length === 0) {
                // Hide tab button and panel completely
                if (tab) {
                    tab.style.display = 'none';
                }
                if (panel) {
                    panel.style.display = 'none';
                }
            } else {
                // Show tab button and panel
                hasAnyProducts = true;
                if (tab) {
                    tab.style.display = '';
                }
                if (panel) {
                    panel.style.display = '';
                }
            }
        });
        
        // Show/hide the entire tabs list container
        const tabsList = this.element.querySelector('.bank-calculator__list');
        if (tabsList) {
            tabsList.style.display = hasAnyProducts ? '' : 'none';
        }
    }
    
    /**
     * Find and activate the first tab that has products for current price
     * If activeTab is already set (from applyDefaultValues), check if it has products first
     */
    activateFirstTabWithProducts() {
        // If activeTab is already set (from applyDefaultValues), check if it has products
        if (this.activeTab) {
            const products = this.getProductsForTab(this.activeTab);
            if (products.length > 0) {
                this.setActiveTab(this.activeTab, false);
                return;
            }
        }
        
        const tabNames = ['installment', 'credit', 'halva', 'leasing'];
        
        for (const tabName of tabNames) {
            const products = this.getProductsForTab(tabName);
            if (products.length > 0) {
                this.activeTab = tabName;
                this.setActiveTab(tabName, false); // false = don't re-render yet
                return;
            }
        }
        
        // No tabs have products - default to first tab
        this.activeTab = 'installment';
    }
    
    async fetchData() {
        try {
            // No reCAPTCHA here on purpose: this endpoint returns public, read-only
            // bank product config. Gating the fetch on the consent-gated reCAPTCHA
            // script delayed data by ~1-2s after cookie acceptance (see decisions.md).
            // Server-side /ajax/index.php exempts payment_calculator from captcha.
            if (!BX.bitrix_sessid()) {
                return false;
            }
            let params = `sessid=${encodeURIComponent(BX.bitrix_sessid())}&controller=${encodeURIComponent('payment_calculator')}&price=${encodeURIComponent(this.price)}&brand=${encodeURIComponent(this.brand)}`;
            const response = await fetch('/ajax/', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded',
                },
                body: params
            });
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            this.ajaxData = await response.json();
            return this.ajaxData;
        } catch (error) {
            console.warn('Failed to fetch AJAX data, using mock data:', error.message);
            this.ajaxData = mockPaymentData;
            return this.ajaxData;
        }
    }
    
    setupTabListeners() {
        this.tabs.forEach(tab => {
            tab.addEventListener('click', () => {
                this.setActiveTab(tab.dataset.tab);
            });
        });
    }
    
    setupFormListeners() {
        // Period and initial payment selects
        this.panels.forEach(panel => {
            const tabType = panel.dataset.panel;
            const periodSelect = panel.querySelector('[name$="-period"]');
            const initialSelect = panel.querySelector('[name$="-initial"]');
            
            if (periodSelect) {
                periodSelect.addEventListener('change', () => {
                    this.currentPeriod = periodSelect.value ? parseInt(periodSelect.value) : null;
                    this.handleInitialPaymentAllowedChange();
                    // Save to tab state
                    this.tabStates[this.activeTab] = {
                        type: this.currentType,
                        period: this.currentPeriod,
                        initial: this.currentInitial
                    };
                    this.calculate();
                });
            }
            
            if (initialSelect) {
                initialSelect.addEventListener('change', () => {
                    this.currentInitial = initialSelect.value ? parseInt(initialSelect.value) : null;
                    // Save to tab state
                    this.tabStates[this.activeTab] = {
                        type: this.currentType,
                        period: this.currentPeriod,
                        initial: this.currentInitial
                    };
                    this.calculate();
                });
            }
        });
        
        // Radio buttons (delegated) - listen for both change and click
        this.element.addEventListener('click', (e) => {
            if (e.target.classList.contains('bank-calculator__radio-input')) {
                this.currentType = e.target.value;

                this.handlePeriodForTypeChange(this.currentType);
                this.handleInitialPaymentAllowedChange();
                // Save to tab state
                this.tabStates[this.activeTab] = {
                    type: this.currentType,
                    period: this.currentPeriod,
                    initial: this.currentInitial
                };
                this.calculate();
            }
        });
        
        this.element.addEventListener('change', (e) => {
            if (e.target.classList.contains('bank-calculator__radio-input')) {
                this.currentType = e.target.value;

                this.handlePeriodForTypeChange(this.currentType);
                this.handleInitialPaymentAllowedChange();
                // Save to tab state
                this.tabStates[this.activeTab] = {
                    type: this.currentType,
                    period: this.currentPeriod,
                    initial: this.currentInitial
                };
                this.calculate();
            }
        });
    }
    
    setActiveTab(tabName, shouldRender = true) {
        // Save current tab state before switching
        if (this.activeTab && this.activeTab !== tabName) {
            this.tabStates[this.activeTab] = {
                type: this.currentType,
                period: this.currentPeriod,
                initial: this.currentInitial
            };
        }
        
        const previousTab = this.activeTab;
        this.activeTab = tabName;
        
        // Update tabs
        this.tabs.forEach(t => {
            t.classList.toggle('bank-calculator__tab_active', t.dataset.tab === tabName);
        });
        
        // Update panels
        this.panels.forEach(p => {
            p.classList.toggle('bank-calculator__panel_active', p.dataset.panel === tabName);
        });
        
        // Try to restore saved state for this tab first
        const savedState = this.tabStates[tabName];
        if (savedState) {
            this.currentType = savedState.type;
            this.currentPeriod = savedState.period;
            this.currentInitial = savedState.initial;
        } else {
            // No saved state - get values from DOM (first render or first visit)
            const panel = this.element.querySelector(`[data-panel="${tabName}"]`);
            const periodSelect = panel?.querySelector('[name$="-period"]');
            const initialSelect = panel?.querySelector('[name$="-initial"]');
            const radioInput = panel?.querySelector('.bank-calculator__radio-input:checked');
            
            this.currentPeriod = periodSelect?.value ? parseInt(periodSelect.value) : null;
            this.currentInitial = initialSelect?.value ? parseInt(initialSelect.value) : null;
            this.currentType = radioInput?.value || null;
        }
        
        // Only re-render if requested (skip during initial auto-activation)
        if (shouldRender) {
            // Re-render options for this tab
            this.renderTabOptions();
            
            // Explicitly ensure the correct type radio is checked after render
            // This is needed because renderTabOptions may have rebuilding logic
            // that affects the radio state
            if (savedState && this.currentType) {
                const radioInput = this.element.querySelector(`[data-panel="${this.activeTab}"] .bank-calculator__radio-input[value="${this.currentType}"]`);
                if (radioInput) {
                    radioInput.checked = true;
                }
            }
        }
        
        // Recalculate
        this.calculate();
    }
    
    renderTabOptions() {
        if (!this.ajaxData) return;
        
        // Get products for this tab type (filtered by price for display)
        const products = this.getProductsForTab(this.activeTab);
        
        // Get ALL products for grouping (no price filter) so groups are complete
        const allProductsForGrouping = this.getAllProductsForTab(this.activeTab);
        
        // Group products (for "Мультибанковская система \"Скрепка\"" functionality) - uses all products
        const groupedProducts = this.getGroupedProducts(allProductsForGrouping);
        
        // Get unique product types from grouped products
        const uniqueTypes = [...new Set(groupedProducts.map(p => p.name))];
        
        // Build dynamic type config based on available product names
        let tabConfig;
        
        if (products.length === 0) {
            // No products available for this tab - leave empty
            tabConfig = {
                [this.activeTab]: { 
                    types: [], 
                    typeLabels: {} 
                }
            };
        } else if (uniqueTypes.length === 1) {
            // Only one product - show its name as the only type option
            tabConfig = {
                [this.activeTab]: { 
                    types: [uniqueTypes[0]], 
                    typeLabels: { [uniqueTypes[0]]: uniqueTypes[0] } 
                }
            };
        } else {
            // Multiple products - show each product name as a type option
            const typeLabels = {};
            uniqueTypes.forEach(name => {
                typeLabels[name] = name;
            });
            tabConfig = {
                [this.activeTab]: { 
                    types: uniqueTypes, 
                    typeLabels: typeLabels 
                }
            };
        }
        
        const config = tabConfig[this.activeTab];
        if (!config) return;
        
        // Render radio buttons
        const radioContainer = this.element.querySelector(`.bank-calculator__radios_${this.activeTab}`);
        if (radioContainer) {
            let html = '';
            config.types.forEach((type, index) => {
                const id = `${this.activeTab}-type-${index + 1}`;
                
                // Get periods for this type
                const periods = this.getPeriodsForType(type);
                const periodsText = periods.length > 0 
                    ? (periods.length === 1 
                        ? `${periods[0]} мес.` 
                        : `от ${Math.min(...periods)} до ${Math.max(...periods)} мес.`) 
                    : '';
                
                // Get image for this type from grouped products
                let imageHtml = '';
                const typeProduct = groupedProducts.find(p => p.name === type);
                if (typeProduct && typeProduct.groupId && this.ajaxData.groupImages?.[typeProduct.groupId]) {
                    const imgData = this.ajaxData.groupImages[typeProduct.groupId];
                    imageHtml = `
                        <picture class="bank-calculator__radio-image">
                            <source srcset="${imgData.srcset}">
                            <img class="bank-calculator__radio-img" src="${imgData.src}" srcset="${imgData.srcset}" alt="${imgData.alt}" loading="lazy" decoding="async">
                        </picture>
                    `;
                }
                
                html += `
                    <div class="bank-calculator__radio">
                        <input type="radio" name="${this.activeTab}-type" value="${type}" 
                               id="${id}" class="bank-calculator__radio-input" data-group-id="${typeProduct?.groupId || ''}" ${index === 0 ? 'checked' : ''}>
                        <label for="${id}" class="bank-calculator__radio-label">
                            ${imageHtml}
                            <span class="bank-calculator__radio-text-wrap">
                                <span class="bank-calculator__radio-text">${config.typeLabels[type]}</span>
                                ${periodsText ? `<span class="bank-calculator__radio-periods">${periodsText}</span>` : ''}
                            </span>
                        </label>
                    </div>
                `;
            });
            radioContainer.innerHTML = html;
        }
        
        // Set initial type - prioritize currentType (which may have been set from defaultProductType/displayName)

        
        // Check if currentType (already converted from groupId to displayName in applyDefaultValues) is valid for this tab
        if (this.currentType && config.types.includes(this.currentType)) {
            // currentType is already set and valid - ensure radio is checked.
            // Scope to the active panel so identical type names in other panels are not matched.
            const radioInput = this.element.querySelector(`[data-panel="${this.activeTab}"] .bank-calculator__radio-input[value="${this.currentType}"]`);
            if (radioInput) {
                radioInput.checked = true;
            }
        } else {
            // currentType is not set OR not valid for this tab - need to select a new type
            let newTypeSelected = false;
            
            if (this.defaultProductType && config.types.length > 0) {
                // Try to convert defaultProductType (groupId) to displayName
                const displayName = this.ajaxData.groupNames?.[this.defaultProductType] || this.defaultProductType;
                if (config.types.includes(displayName)) {
                    this.currentType = displayName;
                    const radioInput = this.element.querySelector(`[data-panel="${this.activeTab}"] .bank-calculator__radio-input[value="${displayName}"]`);
                    if (radioInput) {
                        radioInput.checked = true;
                    }
                    newTypeSelected = true;
                }
            }
            
            // If still not selected, use first available type
            if (!newTypeSelected && config.types.length > 0) {
                this.currentType = config.types[0];
                const radioInput = this.element.querySelector(`[data-panel="${this.activeTab}"] .bank-calculator__radio-input[value="${this.currentType}"]`);
                if (radioInput) {
                    radioInput.checked = true;
                }
            }
        }
        
        // IMPORTANT: Update period dropdown to show only periods for the initially selected type
        // This will also select the first available period
        this.handlePeriodForTypeChange(this.currentType);
        
        // Note: We don't fill period dropdown here again because handlePeriodForTypeChange() already does it
        // This ensures periods are limited to the selected type from the start
        
        // Render initial payment select (no placeholder - starts from 0%)
        // Only render if products exist for this tab
        const initialSelect = this.element.querySelector(`[name="${this.activeTab}-initial"]`);
        if (initialSelect) {
            if (products.length > 0) {
                const step = this.ajaxData.initialPaymentStep;
                const max = this.ajaxData.maxInitialPaymentPercent;
                const initialPayments = generateInitialPayments(step, max);
                
                let optionsHtml = '';
                initialPayments.forEach(percent => {
                    optionsHtml += `<option value="${percent}">${percent}%</option>`;
                });
                initialSelect.innerHTML = optionsHtml;
                
                // Select default value: always try to use saved value from tab state, then defaultInitialFee, then first value (0%)
                if (initialPayments.length > 0) {
                    const savedState = this.tabStates[this.activeTab];
                    const savedInitial = savedState?.initial;
                    
                    // Only restore saved value if a saved state actually exists (savedState !== undefined)
                    // and the saved initial is valid for this tab
                    if (savedState !== undefined && savedInitial !== undefined && savedInitial !== null && initialPayments.includes(savedInitial)) {
                        // Use saved value from tab state
                        initialSelect.value = savedInitial;
                        this.currentInitial = savedInitial;
                    } else if (this.defaultInitialFee > 0 && initialPayments.includes(this.defaultInitialFee)) {
                        initialSelect.value = this.defaultInitialFee;
                        this.currentInitial = this.defaultInitialFee;
                    } else {
                        initialSelect.value = initialPayments[0];
                        this.currentInitial = initialPayments[0];
                    }
                    
                    // Only save to tab state if we're using a default value (not restoring)
                    // This prevents overwriting the saved state with defaults on first visit
                    if (savedState === undefined) {
                        this.tabStates[this.activeTab] = {
                            type: this.currentType,
                            period: this.currentPeriod,
                            initial: this.currentInitial
                        };
                    }
                }
            } else {
                // No products - clear the select
                initialSelect.innerHTML = '';
                this.currentInitial = null;
            }
        }
        
        // Handle initial payment allowed state AFTER dropdown is rendered
        // This ensures options exist in DOM when we try to disable them
        this.handleInitialPaymentAllowedChange();
        
        // Recalculate after initial payment might have been auto-adjusted
        this.calculate();
    }
    
    getProductsForTab(tabType) {
        if (!this.ajaxData) return [];
        
        const typeMapping = {
            installment: ['installment'],
            credit: ['credit', 'credit_combo', 'credit_grace'],
            halva: ['halva'],
            leasing: ['leasing']
        };
        
        const allowedTypes = typeMapping[tabType] || [];
        
        // Return filtered by price for display purposes
        return this.ajaxData.products.filter(p => 
            allowedTypes.includes(p.type) && 
            this.price >= p.minAmount && 
            this.price <= p.maxAmount
        );
    }
    
    /**
     * Get all products for a tab type (without price filtering) - for grouping purposes
     * @param {string} tabType
     * @returns {Object[]}
     */
    getAllProductsForTab(tabType) {
        if (!this.ajaxData) return [];
        
        const typeMapping = {
            installment: ['installment'],
            credit: ['credit', 'credit_combo', 'credit_grace'],
            halva: ['halva'],
            leasing: ['leasing']
        };
        
        const allowedTypes = typeMapping[tabType] || [];
        
        // Return ALL products for this type (no price filtering) - for grouping
        return this.ajaxData.products.filter(p => 
            allowedTypes.includes(p.type)
        );
    }
    
    /**
     * Get periods available for a specific product type
     * @param {string} typeName - the product type name
     * @returns {number[]} - sorted array of available periods
     */
    getPeriodsForType(typeName) {
        const products = this.getAllProductsForTab(this.activeTab);
        const groupedProducts = this.getGroupedProducts(products);
        const typeProduct = groupedProducts.find(p => p.name === typeName);
        
        if (!typeProduct) return [];
        
        // Get all periods for this type (including from group products)
        let periods = [];
        if (typeProduct._groupProducts) {
            periods = typeProduct._groupProducts.map(p => p.period);
        } else {
            periods = [typeProduct.period];
        }
        
        const rawPeriods = [...new Set(periods)].sort((a, b) => a - b);
        
        // Apply periodSteps filter when earlyRepaymentAllowed is true
        const earlyRepaymentAllowed = this.getEarlyRepaymentAllowed(typeProduct.groupId);
        
        if (earlyRepaymentAllowed && this.ajaxData?.periodSteps) {
            // Get max period from available products to limit the range
            const maxPeriod = Math.max(...rawPeriods);
            // Apply periodSteps filter
            return this.getValidPeriodsForMaxPeriod(this.ajaxData.periodSteps, maxPeriod);
        }
        
        // When earlyRepaymentAllowed is false, return raw periods (exact product periods only)
        return rawPeriods;
    }
    
    /**
     * Find nearest available period when current period is not available
     * @param {number} currentPeriod - the period user currently has selected
     * @param {number[]} availablePeriods - array of available periods for new type
     * @returns {number} - the nearest available period
     */
    findNearestPeriod(currentPeriod, availablePeriods) {
        if (availablePeriods.length === 0) return null;
        if (availablePeriods.includes(currentPeriod)) return currentPeriod;
        
        const distances = availablePeriods.map(p => ({
            period: p,
            distance: p - currentPeriod
        }));
        
        // Sort by absolute distance, then by period descending (for equal distances, prefer larger)
        distances.sort((a, b) => {
            const absDiff = Math.abs(a.distance) - Math.abs(b.distance);
            if (absDiff !== 0) return absDiff;
            return b.period - a.period; // larger period first
        });
        
        return distances[0].period;
    }
    
    /**
     * Handle period change when switching product types
     * Updates period dropdown to show only periods available for the new type
     * If current period is not available, auto-selects nearest
     */
    handlePeriodForTypeChange(newType) {
        const availablePeriods = this.getPeriodsForType(newType);
        
        if (availablePeriods.length === 0) {
            this.currentPeriod = null;
            // Clear period select
            const periodSelect = this.element.querySelector(`[name="${this.activeTab}-period"]`);
            if (periodSelect) {
                periodSelect.innerHTML = '';
            }
            return;
        }
        
        // Render period dropdown with only periods available for this type
        const periodSelect = this.element.querySelector(`[name="${this.activeTab}-period"]`);
        if (periodSelect) {
            let optionsHtml = '';
            availablePeriods.forEach(period => {
                optionsHtml += `<option value="${period}">${period} месяцев</option>`;
            });
            periodSelect.innerHTML = optionsHtml;
        }
        
        // Determine which period to select: prioritize defaultPeriod, then currentPeriod, then first available
        let periodToSelect = null;
        
        // First: check if defaultPeriod is available
        if (this.defaultPeriod > 0 && availablePeriods.includes(this.defaultPeriod)) {
            periodToSelect = this.defaultPeriod;
        }
        // Second: check if currentPeriod is available and valid
        else if (this.currentPeriod && availablePeriods.includes(this.currentPeriod)) {
            periodToSelect = this.currentPeriod;
        }
        // Third: find nearest to default or current
        else if (this.defaultPeriod > 0 || this.currentPeriod) {
            const targetPeriod = this.defaultPeriod || this.currentPeriod;
            const nearestPeriod = this.findNearestPeriod(targetPeriod, availablePeriods);
            if (nearestPeriod !== null) {
                periodToSelect = nearestPeriod;
            }
        }
        
        // If no period selected, use first available
        if (periodToSelect === null) {
            periodToSelect = availablePeriods[0];
        }
        
        // Apply the selection
        if (periodSelect) {
            periodSelect.value = String(periodToSelect);
        }
        this.currentPeriod = periodToSelect;
        
        // Save to tab state
        this.tabStates[this.activeTab] = {
            type: this.currentType,
            period: this.currentPeriod,
            initial: this.currentInitial
        };
    }
    
    /**
     * Group products by groupId - products with same groupId should be shown as one type
     * and calculator will select the one with minimum total payment
     * IMPORTANT: Uses ALL products (no price filtering) so groups are complete
     * @param {Object[]} products - should be from getAllProductsForTab()
     * @returns {Object[]} products with group display name
     */
    getGroupedProducts(products) {
        // Group products by groupId
        const groups = {};
        const nonGrouped = [];
        
        products.forEach(p => {
            if (p.groupId) {
                if (!groups[p.groupId]) {
                    groups[p.groupId] = [];
                }
                groups[p.groupId].push(p);
            } else {
                nonGrouped.push(p);
            }
        });
        
        // Create combined product entries for groups
        const result = [];
        Object.keys(groups).forEach(groupId => {
            const groupProducts = groups[groupId];
            // Use groupName from groupNames if available, otherwise use first product's name
            const displayName = this.ajaxData.groupNames?.[groupId] || groupProducts[0].name;
            result.push({
                ...groupProducts[0],
                _groupProducts: groupProducts,
                name: displayName
            });
        });
        
        // Add non-grouped products
        result.push(...nonGrouped);
        
        return result;
    }

    /**
     * Get earlyRepaymentAllowed for a specific group
     * Returns false if not explicitly defined in groupSettings
     * @param {string} groupId - the group ID
     * @returns {boolean}
     */
    getEarlyRepaymentAllowed(groupId) {
        const settings = this.ajaxData?.groupSettings?.[groupId];
        return settings?.earlyRepaymentAllowed === true;
    }
    
    /**
     * Get the selected product based on current type, period, and initial payment
     * For grouped products, returns the grouped product with _groupProducts array
     * @returns {Object|null}
     */
    getSelectedProduct() {


        
        if (!this.currentType || !this.currentPeriod) return null;
        
        // Use getAllProductsForTab to get all products (for grouping)
        let products = this.getAllProductsForTab(this.activeTab);

        
        if (!products || products.length === 0) return null;
        
        // Apply grouping
        products = this.getGroupedProducts(products);

        
        // Get earlyRepaymentAllowed for current type
        const currentProduct = products.find(p => p.name === this.currentType);
        const groupId = currentProduct?.groupId;
        const earlyRepaymentAllowed = this.getEarlyRepaymentAllowed(groupId);
        


        
        if (earlyRepaymentAllowed) {
            // Include selected period and all longer periods (early repayment allowed)
            // Also include grouped products (they have _groupProducts)
            products = products.filter(p => p._groupProducts || p.period >= this.currentPeriod);
        } else {
            // Only exact period match (no early repayment)
            // For grouped products, check if any product in the group has this period
            products = products.filter(p => {
                if (p._groupProducts) {
                    // For grouped products, check if ANY product in the group has this exact period
                    return p._groupProducts.some(gp => gp.period === this.currentPeriod);
                }
                return p.period === this.currentPeriod;
            });
        }
        

        
        if (products.length === 0) return null;
        
        // Filter by product name (currentType is the product name or group name)
        // For grouped products, we need to check if currentType matches either the group name or any product name in the group
        let filteredProducts = products;
        if (this.currentType) {
            // First check if currentType matches any direct product name
            const directMatch = products.find(p => p.name === this.currentType);
            if (directMatch) {
                // Direct match - only return this product
                filteredProducts = [directMatch];
            } else {
                // Check if currentType is a group name
                const groupMatch = products.find(p => 
                    p._groupProducts && p._groupProducts.length > 0 && p.name === this.currentType
                );
                if (groupMatch) {
                    filteredProducts = [groupMatch];
                } else {
                    filteredProducts = [];
                }
            }
        }
        

        return filteredProducts.length > 0 ? filteredProducts[0] : null;
    }
    
    /**
     * Get valid periods based on max product period and periodSteps config
     * Format: "X-Y-Z" where X=start, Y=end (0=infinity), Z=step
     * Example: "12-0-12, 6-24-6, 3-12-3, 1-6-1, 3-3-3"
     * - "12-0-12": from 12 to infinity, step 12 → 12, 24, 36, 48...
     * - "6-24-6": from 6 to 24, step 6 → 6, 12, 18, 24
     * - "3-12-3": from 3 to 12, step 3 → 3, 6, 9, 12
     * - "1-6-1": from 1 to 6, step 1 → 1, 2, 3, 4, 5, 6
     * - "3-3-3": from 3 to 3, step 3 → 3
     * If X >= Y - ignore (error)
     * Result: 1, 2, 3, 4, 5, 6, 9, 12, 18, 24, 36, 48...
     * @param {string} periodStepsStr - e.g., "12-0-12, 6-24-6, 3-12-3"
     * @param {number} maxProductPeriod - maximum period from available products
     * @returns {number[]} - valid periods
     */
    getValidPeriodsForMaxPeriod(periodStepsStr, maxProductPeriod) {
        const parts = periodStepsStr.split(',').map(s => s.trim());
        
        const ranges = []; // Array of {start, end, step}
        
        parts.forEach(part => {
            const values = part.split('-').map(s => Number(s.trim()));
            
            if (values.length === 3) {
                const [start, end, step] = values;
                
                // Skip if any value is not an integer
                if (!Number.isInteger(start) || !Number.isInteger(end) || !Number.isInteger(step)) {
                    return;
                }
                
                // Skip if step <= 0 (error)
                if (step <= 0) {
                    return;
                }
                
                // Skip if X >= Y (error)
                if (start >= end && end !== 0) {
                    return;
                }
                
                // If end is 0, it's infinity - use maxProductPeriod
                const effectiveEnd = end === 0 ? maxProductPeriod : end;
                
                ranges.push({ start, end: effectiveEnd, step });
            }
        });
        
        const periods = new Set();
        
        // Generate periods for each range
        ranges.forEach(range => {
            for (let period = range.start; period <= range.end; period += range.step) {
                if (period <= maxProductPeriod) {
                    periods.add(period);
                }
            }
        });
        
        return Array.from(periods).sort((a, b) => a - b);
    }
    
    /**
     * Handle initial payment allowed state change when switching product types
     * Also handles minInitialPayment blocking - when a product requires minimum initial payment
     */
    handleInitialPaymentAllowedChange() {
        const initialSelect = this.element.querySelector(`[name="${this.activeTab}-initial"]`);
        

        
        if (!initialSelect) {

            return;
        }
        
        // Get products for current type and period
        const products = this.getAllProductsForTab(this.activeTab);
        const groupedProducts = this.getGroupedProducts(products);
        
        // Find the product matching currentType
        const matchedProduct = groupedProducts.find(p => {
            if (p.name === this.currentType) {
                return true;
            }
            if (p._groupProducts && p._groupProducts.length > 0) {
                return p._groupProducts.some(gp => gp.name === this.currentType);
            }
            return false;
        });
        
        if (!matchedProduct) {

            return;
        }
        
        // Get products for current period from the matched product/group
        // Consider earlyRepaymentAllowed - product with period >= currentPeriod is available
        const earlyRepaymentAllowed = this.getEarlyRepaymentAllowed(matchedProduct.groupId);
        
        let productsForPeriod = [];
        if (matchedProduct._groupProducts && matchedProduct._groupProducts.length > 0) {
            // For grouped products, get products that are available for current period
            // When earlyRepaymentAllowed is true, product with period >= currentPeriod is available
            if (earlyRepaymentAllowed) {
                productsForPeriod = matchedProduct._groupProducts.filter(p => p.period >= this.currentPeriod);
            } else {
                productsForPeriod = matchedProduct._groupProducts.filter(p => p.period === this.currentPeriod);
            }
        } else {
            // Non-grouped product - check if it's available for current period
            if (earlyRepaymentAllowed) {
                productsForPeriod = matchedProduct.period >= this.currentPeriod ? [matchedProduct] : [];
            } else {
                productsForPeriod = matchedProduct.period === this.currentPeriod ? [matchedProduct] : [];
            }
        }
        
        
        
        // Step 1: Check if initial payment is allowed (initialPaymentAllowed flag)
        // For grouped products, check if ANY product allows initial payment
        let isAllowed = productsForPeriod.some(p => p.initialPaymentAllowed !== false);
        
        // Step 2: Find minimum minInitialPayment from valid products
        // Only consider products where initialPaymentAllowed !== false
        const validProducts = productsForPeriod.filter(p => p.initialPaymentAllowed !== false);
        const minInitialPaymentValues = validProducts.map(p => p.minInitialPayment || 0);
        const minInitialPayment = minInitialPaymentValues.length > 0 ? Math.min(...minInitialPaymentValues) : 0;
        

        
        // Handle the blocking logic
        if (!isAllowed) {
            // Product doesn't allow initial payment at all
            if (!this.isInitialPaymentBlocked) {
                this.savedInitialPayment = initialSelect.value;
            }
            this.currentInitial = 0;
            initialSelect.value = '0';
            initialSelect.disabled = true;
            this.isInitialPaymentBlocked = true;

        } else if (minInitialPayment > 0) {
            // Product requires minimum initial payment
            // Block values below minInitialPayment and auto-select minInitialPayment
            const currentValue = parseInt(initialSelect.value) || 0;
            
            if (currentValue < minInitialPayment) {
                // Need to auto-select minInitialPayment
                if (!this.isInitialPaymentBlocked) {
                    this.savedInitialPayment = currentValue;
                }
                this.currentInitial = minInitialPayment;
                initialSelect.value = String(minInitialPayment);
                this.isInitialPaymentBlocked = true;

            }
            
            // Disable options below minInitialPayment
            initialSelect.disabled = false;
            
            // Mark options below minInitialPayment as disabled
            const options = initialSelect.querySelectorAll('option');
            options.forEach(opt => {
                const optValue = parseInt(opt.value);
                if (optValue < minInitialPayment) {
                    opt.disabled = true;
                } else {
                    opt.disabled = false;
                }
            });
            
            
        } else {
            // Normal case - initial payment allowed, no minimum required
            if (this.isInitialPaymentBlocked) {
                // Restore from blocked state
                this.currentInitial = this.savedInitialPayment !== null ? this.savedInitialPayment : 0;
                initialSelect.value = String(this.currentInitial);
                this.isInitialPaymentBlocked = false;
                
                // Re-enable all options
                const options = initialSelect.querySelectorAll('option');
                options.forEach(opt => {
                    opt.disabled = false;
                });
                
                // Re-enable the select itself
                initialSelect.disabled = false;
                

            }
            
            // Save to tab state after any adjustments
            this.tabStates[this.activeTab] = {
                type: this.currentType,
                period: this.currentPeriod,
                initial: this.currentInitial
            };
        }
    }
    
    calculate() {
        if (!this.currentPeriod || this.currentInitial === null || this.currentInitial === undefined || !this.currentType) {
            this.updateDisplay(0, 0, 0, 0, 0);
            return;
        }
        
        // Format and display initial price with kopecks
        this.priceEl.textContent = this.formatPrice(this.price);
        
        const initialAmount = (this.price * this.currentInitial) / 100;
        const remainingPrice = this.price - initialAmount;
        
        // Get ALL products for this tab (no price filter for grouping)
        let products = this.getAllProductsForTab(this.activeTab);
        
        // Apply grouping
        products = this.getGroupedProducts(products);
        
        // Get earlyRepaymentAllowed for current type
        const currentProduct = products.find(p => p.name === this.currentType);
        const groupId = currentProduct?.groupId;
        const earlyRepaymentAllowed = this.getEarlyRepaymentAllowed(groupId);
        
        if (earlyRepaymentAllowed) {
            // Include selected period and all longer periods (early repayment allowed)
            // Also include grouped products (they have _groupProducts)
            products = products.filter(p => p._groupProducts || p.period >= this.currentPeriod);
        } else {
            // Only exact period match (no early repayment)
            // For grouped products, check if any product in the group has this period
            products = products.filter(p => {
                if (p._groupProducts) {
                    // For grouped products, check if ANY product in the group has this exact period
                    return p._groupProducts.some(gp => gp.period === this.currentPeriod);
                }
                return p.period === this.currentPeriod;
            });
        }
        
        if (products.length === 0) {
            this.updateDisplay(initialAmount, this.currentPeriod, 0, 0, 0);
            return;
        }
        
        let selectedProduct;
        
        // Filter by product name (currentType is the product/group name)
        // When earlyRepaymentAllowed is false, we need to handle grouped products differently
        // because products are already filtered by exact period
        let filteredProducts = products;
        
        if (this.currentType) {
            // First try direct match
            filteredProducts = products.filter(p => p.name === this.currentType);
            
            // If no match found and this is a grouped product scenario, 
            // check if currentType matches any group that has products for this period
            if (filteredProducts.length === 0) {
                // For grouped products, the currentType might match the group but we already filtered by period
                // So we need to get ALL grouped products (without period filter) and find the one matching currentType
                const allGroupedProducts = this.getGroupedProducts(this.getAllProductsForTab(this.activeTab));
                const groupMatch = allGroupedProducts.find(p => p.name === this.currentType);
                if (groupMatch && groupMatch._groupProducts) {
                    // Find products in this group with the exact period
                    const groupProductForPeriod = groupMatch._groupProducts.find(p => p.period === this.currentPeriod);
                    if (groupProductForPeriod) {
                        selectedProduct = calculateProductPayment(remainingPrice, groupProductForPeriod, this.currentPeriod);
                    }
                }
            }
        }
        
        if (filteredProducts.length > 0) {
            // Check if this is a grouped product (has _groupProducts)
            const firstFiltered = filteredProducts[0];
            
            if (firstFiltered._groupProducts && firstFiltered._groupProducts.length > 0) {
                // For grouped products (like "Мультибанковская система \"Скрепка\""), filter by remainingPrice maxAmount and select minimum total payment
                // Only products that apply to the remaining price should be considered
                // IMPORTANT: Filter each product individually by minInitialPayment - allow products that meet the requirement
                
                // Filter products: each product is valid if its minInitialPayment <= user's initial payment
                // This allows products with minInitialPayment=0 to work even if other products in group require more
                const validGroupProducts = firstFiltered._groupProducts.filter(gp => {
                    const minIP = gp.minInitialPayment || 0;
                    return remainingPrice >= gp.minAmount && 
                        remainingPrice <= gp.maxAmount &&
                        this.currentInitial >= minIP &&
                        (this.currentInitial === 0 ? true : gp.initialPaymentAllowed !== false);
                });
                


                
                if (validGroupProducts.length > 0) {
                    const calculated = validGroupProducts.map(p => 
                        calculateProductPayment(remainingPrice, p, this.currentPeriod)
                    );
                    selectedProduct = findMinOverpaymentProduct(calculated);
                } else {

                }
            } else {
                // Non-grouped product - check if it's valid for remaining price and minInitialPayment
                // Product is valid if: remaining price is within range AND currentInitial >= minInitialPayment
                const minIP = firstFiltered.minInitialPayment || 0;

                if (remainingPrice >= firstFiltered.minAmount && 
                    remainingPrice <= firstFiltered.maxAmount &&
                    this.currentInitial >= minIP) {
                    selectedProduct = calculateProductPayment(remainingPrice, firstFiltered, this.currentPeriod);
                } else {

                }
            }
        } else {
            
        }
        
        if (selectedProduct) {
            // For installment: total payments = only monthly payments (not including initial payment)
            // For credit/leasing: total payments = monthly payments (not including initial)
            const totalPayment = selectedProduct.totalPayment;
            this.updateDisplay(
                initialAmount,
                this.currentPeriod,
                selectedProduct.monthlyPayment,
                totalPayment,
                selectedProduct.discountAmount || 0
            );
        } else {
    
            this.updateDisplay(initialAmount, this.currentPeriod, 0, 0, 0);
        }
    }
    
    formatPrice(num) {
        return Number(num).toLocaleString('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' BYN';
    }
    
    updateDisplay(initial, period, monthly, total, discountAmount) {
        // Format price with 2 decimal places - always show kopecks
        const formatPrice = (num) => {
            return this.formatPrice(num);
        };
        
        this.initialEl.textContent = formatPrice(initial);
        this.periodEl.textContent = period ? `${period} мес.` : '0 мес.';
        this.monthlyEl.textContent = formatPrice(monthly);
        this.totalEl.textContent = formatPrice(total);
        
        // Handle discount display
        // Negative discount = show discount row (it's a real discount reducing price)
        // Positive or zero discount = hide discount row (markup is already included in price)
        if (discountAmount < 0) {
            // Show discount row with absolute value (positive number for display)
            if (this.discountRowEl) {
                this.discountRowEl.style.display = '';
            }
            if (this.discountEl) {
                // Display as positive value for user (they see "-2500 BYN" as "скидка 2500 BYN")
                this.discountEl.textContent = formatPrice(Math.abs(discountAmount));
            }
        } else {
            // Hide discount row (no discount, or it's a markup)
            if (this.discountRowEl) {
                this.discountRowEl.style.display = 'none';
            }
            if (this.discountEl) {
                this.discountEl.textContent = formatPrice(0);
            }
        }
    }
    /**
     * Check if the saved values are available in the calculator
     * @param {string} bankProductGroup - the bank product group (installment, credit, halva, leasing)
     * @param {number} initialFee - the initial fee percentage
     * @param {number} period - the period in months
     * @param {string} productType - optional product type/groupId
     * @returns {boolean} - true if all values are available
     */
    areValuesAvailable(bankProductGroup, initialFee, period, productType = '') {
        if (!bankProductGroup || !this.ajaxData) return false;
        
        // Check if the tab exists and has products
        const products = this.getProductsForTab(bankProductGroup);
        if (products.length === 0) return false;
        
        // Check if the period is available for any product
        const periodsForGroup = this.getPeriodsForGroup(bankProductGroup);
        if (!periodsForGroup.includes(period)) return false;
        
        // Check if initial fee is valid (within available range)
        const step = this.ajaxData.initialPaymentStep;
        const max = this.ajaxData.maxInitialPaymentPercent;
        const availableInitials = generateInitialPayments(step, max);
        
        // Check if the exact initial fee is available
        if (!availableInitials.includes(initialFee)) {
            // Check if there's any initial fee option available
            if (availableInitials.length === 0) return false;
        }
        
        return true;
    }
    
    /**
     * Get all periods available for a group (without price filtering)
     * @param {string} groupName - the bank product group name
     * @returns {number[]} - array of available periods
     */
    getPeriodsForGroup(groupName) {
        if (!this.ajaxData) return [];
        
        // Get all products for this group (no price filter)
        const allProducts = this.ajaxData.products.filter(p => {
            const typeMapping = {
                installment: ['installments'],
                credit: ['credit', 'credit_combo', 'credit_grace'],
                halva: ['halva'],
                leasing: ['leasing']
            };
            return typeMapping[groupName]?.includes(p.type);
        });
        
        // Get unique periods
        const periods = [...new Set(allProducts.map(p => p.period))].sort((a, b) => a - b);
        
        // Apply periodSteps filter if early repayment is allowed
        const groupSettings = this.ajaxData.groupSettings || {};
        let earlyRepaymentAllowed = false;
        
        // Find if any product in group allows early repayment
        for (const [groupId, settings] of Object.entries(groupSettings)) {
            const groupProducts = allProducts.filter(p => p.groupId === groupId);
            if (groupProducts.length > 0 && settings.earlyRepaymentAllowed) {
                earlyRepaymentAllowed = true;
                break;
            }
        }
        
        if (earlyRepaymentAllowed && this.ajaxData?.periodSteps) {
            const maxPeriod = Math.max(...periods);
            return this.getValidPeriodsForMaxPeriod(this.ajaxData.periodSteps, maxPeriod);
        }
        
        return periods;
    }
    
    /**
     * Apply user's saved values to the calculator
     * @param {string} bankProductGroup - the bank product group
     * @param {number} initialFee - the initial fee percentage
     * @param {number} period - the period in months
     * @param {string} productType - optional product type (groupId or displayName)
     * @returns {boolean} - true if values were applied, false if using defaults
     */
    applyUserValues(bankProductGroup, initialFee, period, productType = '') {
        if (!bankProductGroup) {
            return false;
        }
        
        // Check if values are available
        if (!this.areValuesAvailable(bankProductGroup, initialFee, period, productType)) {
            return false;
        }
        
        // Set the active tab
        this.activeTab = bankProductGroup;
        this.setActiveTab(bankProductGroup, true);
        
        // Try to set the type - prioritize productType if provided
        if (productType) {
            // Check if productType is a groupId (try to convert to displayName)
            let displayName = this.ajaxData.groupNames?.[productType] || productType;
            
            // Set currentType to the productType/displayName
            this.currentType = displayName;
            
            // Select the radio button for this type
            const radioContainer = this.element.querySelector(`.bank-calculator__radios_${this.activeTab}`);
            if (radioContainer) {
                const matchingRadio = radioContainer.querySelector(`.bank-calculator__radio-input[value="${displayName}"]`);
                if (matchingRadio) {
                    matchingRadio.checked = true;
                } else {
                    // Try with original productType value
                    const originalRadio = radioContainer.querySelector(`.bank-calculator__radio-input[value="${productType}"]`);
                    if (originalRadio) {
                        originalRadio.checked = true;
                    }
                }
            }
        } else {
            // No productType provided - use first available type
            const products = this.getProductsForTab(bankProductGroup);
            if (products.length > 0) {
                this.currentType = products[0].name;
                
                const radioContainer = this.element.querySelector(`.bank-calculator__radios_${this.activeTab}`);
                if (radioContainer) {
                    const firstRadio = radioContainer.querySelector('.bank-calculator__radio-input');
                    if (firstRadio) {
                        firstRadio.checked = true;
                    }
                }
            }
        }
        
        // Set the period
        this.currentPeriod = period;
        const periodSelect = this.element.querySelector(`[name="${this.activeTab}-period"]`);
        if (periodSelect) {
            // Check if this period is in the dropdown
            const options = Array.from(periodSelect.options).map(opt => parseInt(opt.value));
            if (options.includes(period)) {
                periodSelect.value = String(period);
            }
        }
        
        // Set the initial fee
        this.currentInitial = initialFee;
        const initialSelect = this.element.querySelector(`[name="${this.activeTab}-initial"]`);
        if (initialSelect) {
            const options = Array.from(initialSelect.options).map(opt => parseInt(opt.value));
            if (options.includes(initialFee)) {
                initialSelect.value = String(initialFee);
            } else {
                // Find nearest available initial fee
                const nearest = options.reduce((prev, curr) => 
                    Math.abs(curr - initialFee) < Math.abs(prev - initialFee) ? curr : prev
                );
                initialSelect.value = String(nearest);
                this.currentInitial = nearest;
            }
        }
        
        // Recalculate with the applied values
        this.calculate();
        
        return true;
    }
    
    /**
     * Recalculate with optional initial values
     * @param {Object} initialValues - optional { bankProductGroup, initialFee, period }
     * @returns {boolean} - true if user values were applied, false if defaults used
     */
    recalculate(initialValues) {
        if (initialValues && initialValues.bankProductGroup) {
            return this.applyUserValues(
                initialValues.bankProductGroup,
                initialValues.initialFee,
                initialValues.period
            );
        }
        
        // Use current form values (default behavior)
        this.calculate();
        return false;
    }
    
    /**
     * Get current calculator values
     * @returns {Object} - { bankProductGroup, initialFee, period }
     */
    getCurrentValues() {
        return {
            bankProductGroup: this.activeTab,
            initialFee: this.currentInitial,
            period: this.currentPeriod
        };
    }

    /**
     * Calculate monthly and total payment for explicit parameters.
     * Called by the cart page to fill #cart-bank-calculator summary.
     * Awaits full initialization (AJAX data ready) before calculating.
     *
     * @param {number} price - full product price before initial fee
     * @param {number} initialFee - initial fee percentage (0–100)
     * @param {number} period - payment period in months
     * @param {string} bankProductGroup - tab name: 'installment'|'credit'|'halva'|'leasing'
     * @param {string} productType - groupId (e.g. "skrepka") or display name (e.g. "Мультибанковская система \"Скрепка\"")
     * @returns {Promise<{monthlyPayment: number, totalPayment: number}>}
     */
    async calculatePayment(price, initialFee, period, bankProductGroup, productType) {
        // Wait until init() completes (fetchData + render)
        await this._readyPromise;

        if (!this.ajaxData || !price || !period) {
            return { monthlyPayment: 0, totalPayment: 0 };
        }

        // productType may be a groupId — convert to display name if needed
        const displayName = this.ajaxData.groupNames?.[productType] || productType;

        const initialAmount  = (price * initialFee) / 100;
        const remainingPrice = price - initialAmount;

        let products = this.getAllProductsForTab(bankProductGroup);
        products = this.getGroupedProducts(products);

        const currentProduct = products.find(p => p.name === displayName);
        if (!currentProduct) return { monthlyPayment: 0, totalPayment: 0 };

        const earlyRepaymentAllowed = this.getEarlyRepaymentAllowed(currentProduct.groupId);

        if (earlyRepaymentAllowed) {
            products = products.filter(p => p._groupProducts || p.period >= period);
        } else {
            products = products.filter(p => {
                if (p._groupProducts) return p._groupProducts.some(gp => gp.period === period);
                return p.period === period;
            });
        }

        const filteredProducts = products.filter(p => p.name === displayName);
        if (filteredProducts.length === 0) return { monthlyPayment: 0, totalPayment: 0 };

        let selectedProduct;
        const first = filteredProducts[0];

        if (first._groupProducts && first._groupProducts.length > 0) {
            const validGroupProducts = first._groupProducts.filter(gp => {
                const minIP = gp.minInitialPayment || 0;
                return remainingPrice >= gp.minAmount &&
                       remainingPrice <= gp.maxAmount &&
                       initialFee >= minIP &&
                       (initialFee === 0 ? true : gp.initialPaymentAllowed !== false);
            });
            if (validGroupProducts.length > 0) {
                const calculated = validGroupProducts.map(p =>
                    calculateProductPayment(remainingPrice, p, period)
                );
                selectedProduct = findMinOverpaymentProduct(calculated);
            }
        } else {
            const minIP = first.minInitialPayment || 0;
            if (remainingPrice >= first.minAmount &&
                remainingPrice <= first.maxAmount &&
                initialFee >= minIP) {
                selectedProduct = calculateProductPayment(remainingPrice, first, period);
            }
        }

        if (selectedProduct) {
            return {
                monthlyPayment: selectedProduct.monthlyPayment,
                totalPayment:   selectedProduct.totalPayment
            };
        }
        return { monthlyPayment: 0, totalPayment: 0 };
    }
}

// Initialize on DOM ready
document.addEventListener('DOMContentLoaded', function() {
    const paymentTabsElements = document.querySelectorAll('.bank-calculator');
    paymentTabsElements.forEach((element, index) => {
        const calculator = new BankCalculator(element);
        
        // Expose to global scope for cart functionality
        if (!window.st) window.st = {};
        if (!window.st.bankCalculator) {
            window.st.bankCalculator = calculator;
        }
        
        // Also store by index for multiple calculators
        window.st.bankCalculators = window.st.bankCalculators || [];
        window.st.bankCalculators[index] = calculator;
    });
});
